import { z } from 'zod';
import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { PlayerId } from '@/schemas';

export type Language = 'en' | 'pt-BR';

const ThemeSchema = z.enum(['light', 'dark']);
const LanguageSchema = z.enum(['en', 'pt-BR']);

const getPreferredTheme = (): 'light' | 'dark' => {
  const globalWindow =
    typeof globalThis === 'object' && 'matchMedia' in globalThis
      ? (globalThis as unknown as Window)
      : undefined;

  return globalWindow?.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

const sanitizeTheme = (value: unknown, fallback: 'light' | 'dark'): 'light' | 'dark' => {
  const parsed = ThemeSchema.safeParse(value);
  return parsed.success ? parsed.data : fallback;
};

const sanitizeLanguage = (value: unknown, fallback: Language): Language => {
  const parsed = LanguageSchema.safeParse(value);
  return parsed.success ? parsed.data : fallback;
};

interface UIState {
  isScoreModalOpen: boolean;
  selectedPlayerId: PlayerId | undefined;
  theme: 'light' | 'dark';
  language: Language;
}

interface UIActions {
  openScoreModal: (playerId: PlayerId) => void;
  closeScoreModal: () => void;
  setTheme: (theme: 'light' | 'dark') => void;
  setLanguage: (language: Language) => void;
  reset: () => void;
}

type UIStore = UIState & UIActions;

export const useUIStore = create<UIStore>()(
  devtools(
    persist(
      immer((set) => ({
        // Initial state
        isScoreModalOpen: false,
        selectedPlayerId: undefined,
        theme: getPreferredTheme(),
        language: 'en',

        // Open score modal for a specific player
        openScoreModal: (playerId: PlayerId) => {
          set((draft) => {
            draft.isScoreModalOpen = true;
            draft.selectedPlayerId = playerId;
          });
        },

        // Close score modal
        closeScoreModal: () => {
          set((draft) => {
            draft.isScoreModalOpen = false;
            draft.selectedPlayerId = undefined;
          });
        },

        // Set theme
        setTheme: (theme: 'light' | 'dark') => {
          set((draft) => {
            draft.theme = theme;
          });
          // Sync with DOM for Tailwind dark mode
          const root = document.documentElement;
          root.classList.toggle('dark', theme === 'dark');
        },

        // Set language
        setLanguage: (language: Language) => {
          set((draft) => {
            draft.language = language;
          });
        },

        reset: () => {
          const preferredTheme = getPreferredTheme();
          set((draft) => {
            draft.isScoreModalOpen = false;
            draft.selectedPlayerId = undefined;
            draft.theme = preferredTheme;
            draft.language = 'en';
          });
          if (typeof document !== 'undefined') {
            const root = document.documentElement;
            root.classList.toggle('dark', preferredTheme === 'dark');
          }
        },
      })),
      {
        name: 'ui-storage',
        partialize: (state) => ({
          language: state.language,
          theme: state.theme,
        }),
        merge: (persistedState, currentState) => {
          if (
            !persistedState ||
            typeof persistedState !== 'object' ||
            !('state' in persistedState) ||
            typeof persistedState.state !== 'object' ||
            persistedState.state === null
          ) {
            return currentState;
          }

          const stored = persistedState.state as Record<string, unknown>;

          const language = sanitizeLanguage(stored.language, currentState.language);
          const theme = sanitizeTheme(stored.theme, currentState.theme);

          return {
            ...currentState,
            language,
            theme,
          };
        },
      },
    ),
    { name: 'UIStore' },
  ),
);
